import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import QRCode from 'qrcode';
import eventService from '../services/eventService';

const QRCodeGenerator = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const eventState = useSelector((state) => state.event || {});
  const selectedCenter = eventState.selectedCenter;

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      // Default to center_Id 1 if no center is selected
      const centerId = selectedCenter?.center_Id || 1;
      const response = await eventService.getEvents(centerId);
      setEvents(response || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (url) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'H',
        type: 'image/png',
        quality: 1,
        margin: 2,
        width: 500,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    const eventUrl = `${window.location.origin}/#/event-details/${event.event_Id}`;
    setCustomUrl(eventUrl);
    generateQRCode(eventUrl);
  };

  const handleCustomUrlGenerate = () => {
    if (customUrl) {
      generateQRCode(customUrl);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement('a');
    const filename = selectedEvent 
      ? `qr-code-${selectedEvent.event_Name.replace(/\s+/g, '-')}.png`
      : 'qr-code.png';
    link.download = filename;
    link.href = qrCodeUrl;
    link.click();
  };

  const printQRCode = () => {
    if (!qrCodeUrl) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print QR Code</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            text-align: center;
            padding: 40px;
          }
          .header {
            margin-bottom: 30px;
          }
          .logo {
            max-width: 150px;
            margin-bottom: 20px;
          }
          h1 {
            color: #2c3e50;
            margin: 10px 0;
          }
          .qr-container {
            margin: 30px 0;
          }
          .qr-code {
            max-width: 400px;
            border: 2px solid #ddd;
            padding: 20px;
            border-radius: 10px;
          }
          .url {
            font-size: 18px;
            color: #667eea;
            margin: 20px 0;
            word-break: break-all;
          }
          .instructions {
            font-size: 16px;
            color: #7f8c8d;
            margin-top: 30px;
          }
          @media print {
            body { padding: 20px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="https://sevastorage.blob.core.windows.net/seva-blob/Yugalkunj_website/logo.png" alt="Yugal Kunj Logo" class="logo">
          <h1>${selectedEvent ? selectedEvent.event_Name : 'Yugal Kunj Events'}</h1>
          ${selectedEvent ? `<p>${new Date(selectedEvent.event_Date).toLocaleDateString()}</p>` : ''}
        </div>
        <div class="qr-container">
          <img src="${qrCodeUrl}" alt="QR Code" class="qr-code">
        </div>
        <div class="url">${customUrl}</div>
        <div class="instructions">
          <p>📱 Scan this QR code to register for the event</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            QR Code Generator
          </h1>
          <p className="text-gray-600">
            Generate QR codes for events or custom URLs
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Left Panel - Event Selection */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Select Event
            </h2>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {events.map((event) => (
                  <div
                    key={event.event_Id}
                    onClick={() => handleEventSelect(event)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedEvent?.event_Id === event.event_Id
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <h3 className="font-semibold text-gray-800">
                      {event.event_Name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {new Date(event.event_Date).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Or Generate Custom URL
              </h3>
              <input
                type="text"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="Enter custom URL"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-600 focus:outline-none"
              />
              <button
                onClick={handleCustomUrlGenerate}
                className="w-full mt-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Generate QR Code
              </button>
            </div>
          </div>

          {/* Right Panel - QR Code Display */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Generated QR Code
            </h2>

            {qrCodeUrl ? (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-100 to-blue-100 p-8 rounded-xl">
                  <img
                    src={qrCodeUrl}
                    alt="QR Code"
                    className="w-full max-w-md mx-auto bg-white p-4 rounded-lg shadow-md"
                  />
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">URL:</p>
                  <p className="text-sm text-purple-600 font-mono break-all">
                    {customUrl}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={downloadQRCode}
                    className="flex items-center justify-center gap-2 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </button>
                  <button
                    onClick={printQRCode}
                    className="flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-16 text-gray-400">
                <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                <p className="text-lg">Select an event or enter a custom URL to generate a QR code</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
